import * as React from 'react';

export interface UseDrawerHistoryOptions {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disableHistory?: boolean;
}

interface DrawerStackEntry {
  id: string;
  pathname: string;
  close: () => void;
}

const drawerStack: DrawerStackEntry[] = [];
const closedByPopStateIds = new Set<string>();

let ignorePopStateCount = 0;
let pendingPops = 0;
let popScheduled = false;
let isListenerAttached = false;
let suppressPopForNavigation = false;

export function dismissDrawerHistoryForNavigation() {
  suppressPopForNavigation = true;
  pendingPops = 0;
  popScheduled = false;
  drawerStack.length = 0;
  closedByPopStateIds.clear();
  setTimeout(() => {
    suppressPopForNavigation = false;
  }, 300);
}

function onPopState() {
  if (ignorePopStateCount > 0) {
    ignorePopStateCount--;
    return;
  }

  if (drawerStack.length > 0) {
    const top = drawerStack.pop();
    if (top) {
      closedByPopStateIds.add(top.id);
      top.close();
    }
  }
}

function ensurePopStateListener() {
  if (isListenerAttached || globalThis.window === undefined) {
    return;
  }
  isListenerAttached = true;
  if ('scrollRestoration' in globalThis.window.history) {
    globalThis.window.history.scrollRestoration = 'manual';
  }
  globalThis.window.addEventListener('popstate', onPopState);
}

function scheduleHistoryPop() {
  if (suppressPopForNavigation) {
    return;
  }
  pendingPops++;
  if (!popScheduled) {
    popScheduled = true;
    queueMicrotask(() => {
      popScheduled = false;
      if (suppressPopForNavigation) {
        pendingPops = 0;
        return;
      }
      const count = pendingPops;
      pendingPops = 0;
      if (count > 0 && globalThis.window !== undefined) {
        ignorePopStateCount += count;
        if (count === 1) {
          globalThis.window.history.back();
        } else {
          globalThis.window.history.go(-count);
        }
      }
    });
  }
}

/**
 * Reset internal state for test isolation.
 */
export function _resetDrawerHistoryForTests() {
  drawerStack.length = 0;
  closedByPopStateIds.clear();
  ignorePopStateCount = 0;
  pendingPops = 0;
  popScheduled = false;
}

export function _getDrawerStackDepthForTests() {
  return drawerStack.length;
}

/**
 * Hook that connects a Drawer to the browser History API.
 * When the drawer opens, a history state is pushed.
 * When the user performs a back swipe / gesture from the edge (or presses Back),
 * the drawer closes without leaving the page.
 * When the drawer is closed via UI, the pushed history state is cleaned up.
 */
export function useDrawerHistory({
  open,
  defaultOpen,
  onOpenChange,
  disableHistory = false,
}: UseDrawerHistoryOptions) {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const isOpen = isControlled ? Boolean(open) : uncontrolledOpen;

  const onOpenChangeRef = React.useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const handleClose = React.useCallback(() => {
    if (!isControlled) {
      setUncontrolledOpen(false);
    }
    onOpenChangeRef.current?.(false);
  }, [isControlled]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChangeRef.current?.(nextOpen);
    },
    [isControlled],
  );

  const sessionIdRef = React.useRef<string | null>(null);
  const openedPathnameRef = React.useRef<string>('');

  React.useEffect(() => {
    if (disableHistory || globalThis.window === undefined) {
      return;
    }

    ensurePopStateListener();

    if (isOpen) {
      if (!sessionIdRef.current) {
        const sessionId = `drawer_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        sessionIdRef.current = sessionId;
        openedPathnameRef.current = globalThis.window.location.pathname;

        const currentState = globalThis.window.history.state;
        globalThis.window.history.pushState(
          {
            ...(typeof currentState === 'object' && currentState !== null
              ? currentState
              : {}),
            __drawer_id: sessionId,
          },
          '',
        );

        drawerStack.push({
          id: sessionId,
          pathname: openedPathnameRef.current,
          close: handleClose,
        });
      }
    } else if (sessionIdRef.current) {
      const sessionId = sessionIdRef.current;
      sessionIdRef.current = null;

      const index = drawerStack.findIndex((e) => e.id === sessionId);
      if (index !== -1) {
        drawerStack.splice(index, 1);
      }

      if (closedByPopStateIds.has(sessionId)) {
        closedByPopStateIds.delete(sessionId);
      } else if (
        !suppressPopForNavigation &&
        globalThis.window.location.pathname === openedPathnameRef.current
      ) {
        scheduleHistoryPop();
      }
    }
  }, [isOpen, disableHistory, handleClose]);

  // Clean up if unmounted while still open
  React.useEffect(() => {
    return () => {
      if (sessionIdRef.current) {
        const sessionId = sessionIdRef.current;
        sessionIdRef.current = null;

        const index = drawerStack.findIndex((e) => e.id === sessionId);
        if (index !== -1) {
          drawerStack.splice(index, 1);
        }

        if (closedByPopStateIds.has(sessionId)) {
          closedByPopStateIds.delete(sessionId);
        } else if (
          !suppressPopForNavigation &&
          globalThis.window !== undefined &&
          globalThis.window.location.pathname === openedPathnameRef.current
        ) {
          scheduleHistoryPop();
        }
      }
    };
  }, []);

  if (disableHistory) {
    return {
      open,
      onOpenChange,
    };
  }

  return {
    open: isOpen,
    onOpenChange: handleOpenChange,
  };
}
